import React from "react";
import { useMutation } from "react-apollo";
import Rate from "rc-rate";
import {
  Button,
  Col,
  FormGroup,
  Modal,
  ModalBody,
  Spinner,
  Container,
  Row,
  FormFeedback,
  Input,
  Form,
  Label,
} from "reactstrap";
import { useNotification } from "../../lib/context/NotificationContext/NotificationContext";
import { ORDER_FEEDBACK } from "../../queries/mutations/generalMutation";
import {
  errors,
  formMessages,
  formValidation,
} from "../../lib/translation/strings";
import { useTranslation } from "react-i18next";
import { useOrderFeedbackFormik } from "../../lib/formik/useOrderFeedback";
import { useRouter } from "../../lib/hooks/useRouter";
import { useAuthContext } from "../../lib/context/AuthContext/AuthContext";
import {
  getLoggedInDatabaseId,
  getLoggedInUserGid,
} from "../../lib/helpers/authUserIds";

import "rc-rate/assets/index.css";
import "./OrderFeedbackModal.scss";

interface Props {
  orderId?: number | string;
  open: boolean;
  toggle: () => void;
}

export const OrderFeedbackModal = (props: Props) => {
  const router = useRouter();
  const { t } = useTranslation();
  const ntfCtx = useNotification();
  const authCtx = useAuthContext();
  const { open, toggle, orderId } = props;
  const loggedInUserGid = getLoggedInUserGid(authCtx.user);
  const loggedInUserId = getLoggedInDatabaseId(authCtx.user);

  const [mutation, { loading }] = useMutation(ORDER_FEEDBACK);

  const formik = useOrderFeedbackFormik({
    onSubmit: async (values) => {
      const resolvedOrderId = orderId != null ? String(orderId) : undefined;

      try {
        const res = await mutation({
          variables: {
            message: values.message,
            review: Number(values.review),
            orderId: resolvedOrderId,
            userId: loggedInUserGid || loggedInUserId,
          },
        });

        if (!res?.data?.orderFeedbackForm) {
          ntfCtx.danger(t(errors.wentWrong), true);
          return;
        }

        props.toggle();
        ntfCtx.success(t(formMessages.thanksForComment), true);
        if (resolvedOrderId) {
          router.history.push(`/my-profile/orders/${resolvedOrderId}`);
        }
      } catch (error) {
        ntfCtx.danger(t(errors.wentWrong), true);
      }
    },
  });

  return (
    <Modal isOpen={open} toggle={toggle}>
      <ModalBody>
        <Container>
          <div className="OrderFeedbackModal">
            <h1>Vlersoni porosinë me një koment</h1>
            <Form style={{ marginBottom: 30 }} onSubmit={formik.handleSubmit}>
              <Row>
                <Col xs="12" sm="12">
                  <div className="OrderFeedbackModal__rate">
                    <Label>Vlersimi nga 1 deri në 5</Label>
                    <Rate
                      style={{ fontSize: 30 }}
                      allowHalf={false}
                      value={formik.values.review}
                      onChange={(value: number) =>
                        formik.setFieldValue("review", value)
                      }
                    />
                    {formik.errors.review && formik.touched.review && (
                      <span className="OrderFeedbackModal__rate_error">
                        {formik.errors.review}
                      </span>
                    )}
                  </div>
                </Col>
                <Col xs="12" sm="12">
                  <FormGroup className="form-group">
                    <Label for="message">Pëshkrimi</Label>
                    <Input
                      type="textarea"
                      id="message"
                      name="message"
                      placeholder="Pëshkrimi"
                      className="form-control"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.message}
                      invalid={!!formik.errors.message && formik.touched.message}
                    />
                    <FormFeedback>{formik.errors.message}</FormFeedback>
                  </FormGroup>
                </Col>
                <div className="w-100" />
                <Col xs="12">
                  <FormGroup>
                    <Button
                      color="primary"
                      type="submit"
                      disabled={loading || formik.isSubmitting}
                      className="w-100-mobile OrderFeedbackModal__button"
                    >
                      {loading ? <Spinner size="sm" /> : "Komento"}
                    </Button>
                  </FormGroup>
                </Col>
              </Row>
            </Form>
            <div onClick={props.toggle} className="OrderFeedbackModal__close">
              <span>X</span>
            </div>
          </div>
        </Container>
      </ModalBody>
    </Modal>
  );
};
